import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import { hasEntityBankAccounts, hasEntityCards, hasEntityInvestorProfile, hasEntityUsLegalStatus } from '@/entities/utils/has-entity';
import useDocuments from '@/entity/hooks/use-documents';
import useEntityOwnedBusinesses from '@/entity/hooks/use-entity-owned-businesses';
import AddressFieldset from '../address-fieldset';
import CustomDataFields from '../custom-data-fields';
import DocumentFieldset from '../document-fieldset';
import Fieldset from '../fieldset';
import GridLayout, { type Section, SectionKeys } from '../grid-layout';
import FinancialFieldset from './components/financial-fieldset';
import InvestorProfileFields from './components/investor-profile-fields';
import OwnedBusinesses from './components/owned-businesses';
import useFieldsets from './hooks/use-fieldsets';
type PersonVaultProps = {
  entity: Entity;
};
const PersonVault = t0 => {
  const $ = _c(47);
  const {
    entity
  } = t0;
  let t1;
  if ($[0] !== entity) {
    t1 = hasEntityCards(entity);
    $[0] = entity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasCards = t1;
  let t2;
  if ($[2] !== entity) {
    t2 = hasEntityBankAccounts(entity);
    $[2] = entity;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasBankAccounts = t2;
  let t3;
  if ($[4] !== entity) {
    t3 = hasEntityInvestorProfile(entity);
    $[4] = entity;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasInvestorProfile = t3;
  let t4;
  if ($[6] !== entity) {
    t4 = hasEntityUsLegalStatus(entity);
    $[6] = entity;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const hasUsLegalStatus = t4;
  const {
    data: documents
  } = useDocuments(entity.id);
  const hasDocuments = documents && documents.length > 0;
  let t5;
  if ($[8] !== entity.data) {
    t5 = entity.data.some(_temp);
    $[8] = entity.data;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const hasCustomData = t5;
  const {
    basic,
    address,
    usLegalStatus,
    identity,
    investorProfile,
    custom
  } = useFieldsets(!hasUsLegalStatus);
  const {
    ownedBusinesses,
    hasBusinesses
  } = useEntityOwnedBusinesses(entity.id);
  const hasFinancial = hasCards || hasBankAccounts;
  let t6;
  if ($[10] !== basic.fields || $[11] !== basic.title) {
    t6 = {
      key: SectionKeys.BASIC,
      content: <Fieldset fields={basic.fields} title={basic.title} />
    };
    $[10] = basic.fields;
    $[11] = basic.title;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== address.fields || $[14] !== address.title) {
    t7 = {
      key: SectionKeys.ADDRESS,
      content: <AddressFieldset fields={address.fields} title={address.title} />
    };
    $[13] = address.fields;
    $[14] = address.title;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== identity.fields || $[17] !== identity.title) {
    t8 = {
      key: SectionKeys.IDENTITY,
      content: <Fieldset fields={identity.fields} title={identity.title} />
    };
    $[16] = identity.fields;
    $[17] = identity.title;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== hasUsLegalStatus || $[20] !== usLegalStatus) {
    t9 = hasUsLegalStatus && {
      key: SectionKeys.US_LEGAL,
      content: <Fieldset fields={usLegalStatus.fields} title={usLegalStatus.title} />
    };
    $[19] = hasUsLegalStatus;
    $[20] = usLegalStatus;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== documents || $[23] !== hasDocuments) {
    t10 = hasDocuments && {
      key: SectionKeys.DOCUMENTS,
      content: <DocumentFieldset documents={documents} />
    };
    $[22] = documents;
    $[23] = hasDocuments;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== hasFinancial) {
    t11 = hasFinancial && {
      key: SectionKeys.FINANCIAL,
      content: <FinancialFieldset />
    };
    $[25] = hasFinancial;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== hasBusinesses || $[28] !== ownedBusinesses) {
    t12 = hasBusinesses && {
      key: SectionKeys.BUSINESS_OWNERS,
      content: <OwnedBusinesses businesses={ownedBusinesses} />
    };
    $[27] = hasBusinesses;
    $[28] = ownedBusinesses;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== custom || $[31] !== entity || $[32] !== hasCustomData) {
    t13 = hasCustomData && {
      key: SectionKeys.CUSTOM,
      content: <CustomDataFields entity={entity} title={custom.title} />
    };
    $[30] = custom;
    $[31] = entity;
    $[32] = hasCustomData;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== hasInvestorProfile || $[35] !== investorProfile) {
    t14 = hasInvestorProfile && {
      key: SectionKeys.INVESTOR,
      content: <Fieldset fields={investorProfile.fields} title={investorProfile.title}><InvestorProfileFields /></Fieldset>
    };
    $[34] = hasInvestorProfile;
    $[35] = investorProfile;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t13 || $[41] !== t14 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    const sections = [t6, t7, t8, t9, t10, t11, t12, t13, t14].filter(_temp2);
    t15 = <GridLayout sections={sections} />;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
};
export default PersonVault;
function _temp(attr) {
  return attr.identifier.startsWith("custom");
}
function _temp2(section) {
  return Boolean(section);
}